/**
 * Loading stand-in for a spot that hasn't arrived yet. Reproduces the
 * SpotCard table layout's geometry — same padding, same caption line,
 * the same aspect-ratio felt oval, the same ghost action row — so the
 * frame holds its size and **nothing shifts** when the real spot pops
 * in. The felt is dimmed + label-only ("Dealing your spot…") to read as
 * "this table is being set."
 *
 * Mirrors `PokerTable`'s oval dimensions (`aspect-[0.85/1]
 * md:aspect-[1.4/1]`, max 620px) and `SpotCard`'s table-layout wrapper
 * (`p-4 sm:p-5 space-y-3`). If either changes, match it here.
 */

import { seatPositionPct } from "@/features/chat/lib/seatLayout";
import { LogoMark } from "@/shared/components/LogoMark";

export function SpotSkeleton({
  label = "Dealing your spot…",
}: {
  label?: string;
}) {
  return (
    <div className="relative p-4 sm:p-5 space-y-3">
      {/* Caption placeholder — approximates the question line so the
          oval sits at the same vertical offset as the real card. */}
      <div className="flex justify-center pt-0.5">
        <div className="h-3.5 w-3/5 max-w-sm rounded-full bg-text/5 animate-pulse" />
      </div>

      {/* Felt oval — same box + radius as PokerTable, dimmed. */}
      <div
        className="relative w-full mx-auto aspect-[0.85/1] md:aspect-[1.4/1]"
        style={{ maxWidth: "620px" }}
      >
        <div
          className="absolute inset-0 rounded-[50%/35%]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 45%, #0b1530 0%, #050811 80%)",
            border: "2px solid rgba(94, 123, 232, 0.35)",
            boxShadow:
              "inset 0 0 50px rgba(124, 147, 241, 0.10), 0 28px 60px -24px rgba(0, 0, 0, 0.85)",
          }}
        />

        {/* Ghost seat pills around the perimeter — no labels, just enough
            to read as a table mid-deal. */}
        {Array.from({ length: 9 }, (_, i) => {
          const { left, top } = seatPositionPct(i);
          return (
            <div
              key={i}
              className="absolute rounded-full bg-text/5"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                transform: "translate(-50%, -50%)",
                width: "46px",
                height: "22px",
              }}
            />
          );
        })}

        {/* Center label. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-text-muted">
          <LogoMark size={34} animate />
          <span className="text-sm animate-pulse">{label}</span>
        </div>
      </div>

      {/* Ghost action row — holds the action-bar height. */}
      <div className="flex justify-center gap-2 pt-1">
        <div className="h-11 w-20 rounded-lg bg-text/5 animate-pulse" />
        <div className="h-11 w-24 rounded-lg bg-text/5 animate-pulse" />
        <div className="h-11 w-20 rounded-lg bg-text/5 animate-pulse" />
      </div>
    </div>
  );
}
